/**
 * ListTable renderer (Req 2.2, 2.6).
 *
 * Row convention:
 *  - When `columns` is present a <table> is rendered. Each row is read as a
 *    cell source per column:
 *      * object row  -> cell = row[column]   (keyed by column name)
 *      * array row   -> cell = row[colIndex] (array-index aligned)
 *      * scalar row  -> the value is placed in the first column only
 *  - When `columns` is absent a simple bulleted list is rendered; non-string
 *    rows are stringified readably.
 */
import type { ListTablePayload } from './types';

function toDisplayString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function cellFor(row: unknown, column: string, colIndex: number): string {
  if (Array.isArray(row)) return toDisplayString(row[colIndex]);
  if (row && typeof row === 'object') {
    return toDisplayString((row as Record<string, unknown>)[column]);
  }
  // scalar row: only surface it under the first column
  return colIndex === 0 ? toDisplayString(row) : '';
}

export function ListTable({ title, payload, accent }: { title?: string; payload: ListTablePayload; accent: string }) {
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  const columns = Array.isArray(payload.columns) ? payload.columns : undefined;

  return (
    <div className="rounded-lg bg-slate-900 p-5">
      {title ? (
        <h2 className="text-xl font-semibold mb-4" style={{ color: accent }}>
          {title}
        </h2>
      ) : null}

      {rows.length === 0 ? (
        <p className="text-slate-500">No rows.</p>
      ) : columns && columns.length > 0 ? (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-700">
              {columns.map((col, i) => (
                <th key={i} className="py-2 pr-4 font-medium" style={{ color: accent }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-slate-800">
                {columns.map((col, ci) => (
                  <td key={ci} className="py-2 pr-4 text-slate-100">
                    {cellFor(row, col, ci)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="list-disc pl-5 space-y-1">
          {rows.map((row, i) => (
            <li key={i} className="text-sm text-slate-100">
              {toDisplayString(row)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
